import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import SlideOverModalLayout from '../modals/SlideOverModalLayout';
import { Avatar } from '../Avatar';
import { MarkAsReadButton } from '../MarkAsReadButton';
import { ChatPanel } from '../patient/ChatPanel';
import { PatientChatProvider, useChatApiVersion } from '@odo/shared-ui';
import { getChatService } from '../../services/chatService';
import { usePatientMetadata } from '../../providers/PatientMetadataProvider';
import { useToast } from '../../providers/ToastProvider';
import { markChatAsSeen, markChatAsUnread } from '../../services/patient';
import { getNameClickRoute } from '../../utils/actionRoutes';

interface PatientInfo {
    id: string;
    name: string;
    treatmentStatus: string | null;
    startDate: string | null;
    alignerNumber: number | null;
    alignerTotal: number | null;
    requiredAction: string | null;
}

interface ConversationPanelProps {
    patient: PatientInfo;
    onClose: () => void;
}

const ConversationPanelContent: React.FC<{
    patient: PatientInfo;
    onClose: () => void;
}> = ({ patient, onClose }) => {
    const navigate = useNavigate();
    const {
        getProfilePictureUrl,
        hasUnreadMessages,
        getEntry,
        refresh: refreshMetadata
    } = usePatientMetadata();
    const { showToast } = useToast();
    const chatOpenRef = useRef<(() => void) | null>(null);
    const [isMarkingRead, setIsMarkingRead] = useState(false);
    const [isMarkingUnread, setIsMarkingUnread] = useState(false);

    const profilePicUrl = getProfilePictureUrl(patient.id) ?? '';
    const isUnread = hasUnreadMessages(patient.id);
    const reviewedBy = getEntry(patient.id)?.reviewedBy ?? null;

    useEffect(() => {
        chatOpenRef.current?.();
    }, []);

    const handleMarkAsDone = useCallback(async () => {
        setIsMarkingRead(true);
        try {
            await markChatAsSeen(patient.id);
            await refreshMetadata();
        } catch {
            showToast('Failed to mark as read', 'error');
        } finally {
            setIsMarkingRead(false);
        }
    }, [patient.id, refreshMetadata, showToast]);

    const handleMarkAsUnread = useCallback(async () => {
        setIsMarkingUnread(true);
        try {
            await markChatAsUnread(patient.id);
            await refreshMetadata();
        } catch {
            showToast('Failed to mark as unread', 'error');
        } finally {
            setIsMarkingUnread(false);
        }
    }, [patient.id, refreshMetadata, showToast]);

    return (
        <SlideOverModalLayout onClose={onClose}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
                <button
                    onClick={() => navigate(getNameClickRoute(patient.requiredAction, patient.id))}
                    className="flex items-center gap-3 min-w-0 group hover:opacity-80 transition-opacity focus:outline-none"
                >
                    <div className="w-9 h-9 flex-shrink-0">
                        <Avatar
                            user={{ name: patient.name || 'N/A', email: '' }}
                            imageURL={profilePicUrl}
                        />
                    </div>
                    <div className="min-w-0 text-left">
                        <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold text-gray-800 truncate group-hover:underline leading-tight">
                                {patient.name}
                            </span>
                            <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
                        </div>
                        {patient.treatmentStatus && (
                            <span className="text-xs text-gray-500 truncate block leading-tight">
                                {patient.treatmentStatus}
                            </span>
                        )}
                    </div>
                </button>
                {isUnread ? (
                    <MarkAsReadButton onClick={handleMarkAsDone} isLoading={isMarkingRead} />
                ) : (
                    <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                            <CheckCircleIcon className="w-3.5 h-3.5" />
                            {reviewedBy ? `Reviewed by ${reviewedBy}` : 'Reviewed'}
                        </span>
                        <button
                            onClick={handleMarkAsUnread}
                            disabled={isMarkingUnread}
                            className="flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 hover:bg-orange-100 transition-colors disabled:opacity-50"
                        >
                            {isMarkingUnread ? (
                                <div className="h-3.5 w-3.5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                                'Mark as unread'
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Chat — search, file upload, AI typing. The panel header already has its
          own "Mark as read" control, so suppress the chat's embedded banner. */}
            <ChatPanel
                showMarkAsSeenBanner={false}
                openRef={chatOpenRef}
                className="flex-1 min-h-0 flex flex-col overflow-hidden bg-white"
            />
        </SlideOverModalLayout>
    );
};

const ConversationPanel: React.FC<ConversationPanelProps> = ({ patient, onClose }) => {
    // Remounting on the version is not cosmetic: v1 ids are Monday update ids
    // and v2 ids are UUIDs, so the provider must rebuild rather than reconcile
    // its cached messages onto ids from the other world.
    const { version: chatApiVersion } = useChatApiVersion();
    return (
        <PatientChatProvider
            patientId={patient.id}
            key={chatApiVersion}
            service={getChatService(chatApiVersion)}
        >
            <ConversationPanelContent patient={patient} onClose={onClose} />
        </PatientChatProvider>
    );
};

export default ConversationPanel;
